import { useEffect, useState, useRef } from "react";
import { Mail, Users, MapPin, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/api/client";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short" });
  } catch (_e) {
    return "—";
  }
}

/** AhjContactsBadge — small pill that surfaces "You've worked with N people here"
 * on County/Place detail. Hover (or click on touch) opens a popover with the
 * top 3 contacts + a deep-link into the full AHJ Playbook related-comms panel.
 */
export default function AhjContactsBadge({ ahjKey, scope = "county", playbookAnchorId = "ahj-playbook" }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    if (!ahjKey) return;
    (async () => {
      try {
        const { data } = await api.get(`/ahj/contacts/preview/${ahjKey}`, { params: { scope } });
        if (!cancelledRef.current) setData(data);
      } catch (_e) {
        if (!cancelledRef.current) setData({ total_contacts: 0, top: [] });
      }
    })();
    return () => { cancelledRef.current = true; };
  }, [ahjKey, scope]);

  // Hide when no contacts at all — keeps the page clean for jurisdictions
  // without ingested communications.
  if (!data || data.total_contacts === 0) return null;

  const n = data.total_contacts;
  const msgs = data.total_messages;
  const scrollToPlaybook = (e) => {
    e?.preventDefault?.();
    const el = document.getElementById(playbookAnchorId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Briefly flash the panel so users see the connection
      el.classList.add("ring-2", "ring-emerald-300");
      setTimeout(() => el.classList.remove("ring-2", "ring-emerald-300"), 1400);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
          onClick={() => setOpen((v) => !v)}
          data-testid={`ahj-contacts-badge-${ahjKey}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors px-3 py-1 text-xs font-medium text-emerald-800 cursor-pointer"
        >
          <Users className="w-3.5 h-3.5" />
          You've worked with <span className="font-semibold tabular-nums">{n}</span> {n === 1 ? "contact" : "contacts"} here
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 overflow-hidden"
        align="start"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        data-testid={`ahj-contacts-badge-popover-${ahjKey}`}
      >
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">AHJ Contacts</div>
          <div className="font-outfit text-sm font-semibold text-slate-900 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-emerald-600" />
            {data.top?.[0]?.jurisdiction_label || "Linked from your mailbox"}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
            {n} {n === 1 ? "person" : "people"} · {msgs} message{msgs === 1 ? "" : "s"}
          </div>
        </div>
        <ul className="divide-y divide-slate-100" data-testid={`ahj-contacts-badge-list-${ahjKey}`}>
          {data.top.map((c, i) => (
            <li key={c.email} className="px-4 py-2.5 flex items-start gap-2" data-testid={`ahj-contact-item-${i}`}>
              <Mail className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-none" />
              <div className="min-w-0 flex-1">
                <div className="font-mono text-xs text-slate-900 truncate">{c.email}</div>
                {c.name ? (
                  <div className="text-[11px] text-slate-500 truncate">{c.name}</div>
                ) : null}
                <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                  {c.count} msg{c.count === 1 ? "" : "s"} · last {fmtDate(c.latest_date_iso)}
                </div>
              </div>
            </li>
          ))}
        </ul>
        {n > data.top.length && (
          <button
            type="button"
            onClick={scrollToPlaybook}
            className="w-full px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium flex items-center justify-center gap-1.5 transition-colors"
            data-testid={`ahj-contacts-view-all-${ahjKey}`}
          >
            View all {n} in the Playbook <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
